import { toStatus } from '#~/device/index.js';
// Transitory-placeholder slice (Multi-Add Phase 2, spec §2.1): a transitory
// id is never in `room.devices` (agent authority) NOR in `pendingDeviceIds`,
// so this sidebar list must synthesize the device-object from the placeholder
// — the SAME `toSyntheticDevice` seam the main-grid `<Annotation>` already
// uses (`content/annotation.tsx`). Without it the tile renders on the main
// canvas but silently drops out of the left tray.
// Per `reference-rules-in-comments` (ai.knowledge/global).
import { toSyntheticDevice, useTransitoryDevices } from '#~/device/transitory.js';
import { useArrangement, useDeviceIds, useRoom } from '#~/room/hooks.js';
import { useLazyRef } from '@studio/react-utils/hooks';
import type { T_NotNil } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import { List } from './list.jsx';
import type { Props } from './types.js';
import { useWiring } from './wiring.js';
import { usePendingDeviceIds, type Device } from '@studio/models/device';

/** Video `<Sources>` component. */
export const Sources = (props: Props): ReactElement => {
  const [devices] = useRoom((µ) => [µ.devices], shallow);
  const { videoIds } = useDeviceIds();
  // `useDeviceIds` already merges transitory placeholder ids into `videoIds`
  // (room/hooks.ts `appendAndDedup`), but those ids resolve to neither a real
  // `devices[id]` nor a `pendingDeviceIds[id]`. Read the placeholder slice so
  // `formattedDevices` below can synthesize a renderable tile for them.
  const transitoryDevices = useTransitoryDevices();

  const { selectedId, ...wiring } = useWiring(devices);
  const [arrangement] = useArrangement();

  const pendingDeviceIds = usePendingDeviceIds();

  // Wiring handlers are stable so this is okay!
  const handler = useLazyRef(() => ({
    onAdd: wiring.handleAdd,
    onCalibrate: wiring.handleCalibrate,
    onRemove: wiring.handleRemove,
    onRename: wiring.handleRename,
    onReorder: wiring.handleReorder,
    onSelect: wiring.handleSelect,
    onSettings: wiring.handleSettings,
    onUnlink: wiring.handleUnlink
  })).current;

  const formattedDevices = useMemo(
    () =>
      videoIds
        .map((µ) => {
          // Real, agent-authoritative device — the common path.
          const realDevice = devices[µ];
          if (realDevice) {
            return { ...realDevice, pending: false };
          }

          // Device mid-link: surfaced from `pendingDeviceIds` with a
          // "Loading..." name and `pending: true` so the sort drops it last.
          if (Object.keys(pendingDeviceIds).includes(µ)) {
            return {
              ...pendingDeviceIds[µ],
              name: pendingDeviceIds[µ]?.name ?? 'Loading...',
              pending: true
            } as unknown as Device & { pending: boolean };
          }

          // Transitory placeholder (Multi-Add Phase 2): the id is in
          // `videoIds` (merged by `useDeviceIds`) but is neither a real
          // `devices[id]` nor a `pendingDeviceIds[id]`, so the previous
          // ternary mapped it to `null` and the `.filter` dropped it — the
          // tile rendered on the main canvas yet vanished from this sidebar
          // tray. Synthesize it the SAME way the main-grid `<Annotation>`
          // does (`content/annotation.tsx`: `toSyntheticDevice(placeholder)`)
          // so both surfaces render the tile from one shape.
          // Per `reference-rules-in-comments` (ai.knowledge/global).
          const placeholder = transitoryDevices[µ];
          if (placeholder) {
            return {
              ...toSyntheticDevice(placeholder),
              pending: false
            };
          }

          return null;
        })
        .filter((µ): µ is T_NotNil<typeof µ> => !!µ)
        // preserve existing order but sort so that pending devices appear at the end
        .sort((a, b) => {
          if (a.pending === b.pending) return 0;
          if (a.pending) return 1;
          return -1;
        })
        // @ts-ignore
        .map((d) => ({ ...d, status: toStatus(d) })),
    [devices, videoIds, transitoryDevices]
  );

  // Be very careful w/ memoization here!
  return (
    <List
      arrangement={arrangement}
      // @ts-ignore
      devices={formattedDevices}
      handler={handler}
      selectedId={selectedId}
      {...props}
    />
  );
};
